import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

const InfoRow = ({ label, children, muted }) => (
  <div className="flex items-baseline justify-between py-[5px]">
    <span className="text-[12px] text-[#8993a4] select-none">{label}</span>
    <span className={`text-[13px] font-medium ${muted ? 'text-[#8993a4]' : 'text-[#2d3643]'}`}>
      {children}
    </span>
  </div>
);

const SectionLabel = ({ children }) => (
  <p className="text-[11px] font-semibold text-[#8993a4] uppercase tracking-[0.06em]">
    {children}
  </p>
);

const SubscriptionStateCard = ({ state }) => {
  const { t } = useTranslation();
  const tl = (key) => t(`admin.auditLogs.subscriptionCard.${key}`);

  const [expandedSections, setExpandedSections] = useState({});

  if (!state) {
    return (
      <div className="bg-white border border-[#e5e7eb] rounded-xl p-5">
        <span className="text-[13px] text-[#8993a4]">-</span>
      </div>
    );
  }

  const toggleSection = (key) => {
    setExpandedSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const formatDate = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleString();
    } catch {
      return dateStr;
    }
  };

  const formatValue = (value) => {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'boolean') return value ? tl('yes') : tl('no');
    if (typeof value === 'string' && value.includes('T') && value.includes('Z')) {
      return formatDate(value);
    }
    return String(value);
  };

  return (
    <div className="bg-white border border-[#e5e7eb] rounded-xl overflow-hidden">
      <div className="px-5 py-3">
        {/* Basic fields */}
        {state._id && <InfoRow label={tl('id')}>{state._id}</InfoRow>}
        {state.teamId && <InfoRow label={tl('teamId')}>{state.teamId}</InfoRow>}
        {state.teamName && <InfoRow label={tl('teamName')}>{state.teamName}</InfoRow>}
        {state.tier && <InfoRow label={tl('tier')}>{state.tier}</InfoRow>}
        {state.title && <InfoRow label={tl('title')}>{state.title}</InfoRow>}
        {state.description && (
          <InfoRow label={tl('description')} muted>
            {state.description}
          </InfoRow>
        )}
        {state.isActive !== undefined && (
          <InfoRow label={tl('active')}>{state.isActive ? tl('yes') : tl('no')}</InfoRow>
        )}
        {state.countryCode && <InfoRow label={tl('country')}>{state.countryCode}</InfoRow>}

        {/* Dates */}
        {state.createdAt && (
          <InfoRow label={tl('created')} muted>
            {formatDate(state.createdAt)}
          </InfoRow>
        )}
        {state.updatedAt && (
          <InfoRow label={tl('updated')} muted>
            {formatDate(state.updatedAt)}
          </InfoRow>
        )}

        {state.stripeProductId && (
          <InfoRow label={tl('stripeProductId')}>{state.stripeProductId}</InfoRow>
        )}

        {/* Features Section */}
        {state.features && Array.isArray(state.features) && state.features.length > 0 && (
          <>
            <div className="h-px bg-[#f0f2f4] my-2" />
            <button
              onClick={() => toggleSection('features')}
              className="w-full text-left py-1.5 hover:bg-[#f9fafb] transition-colors rounded px-1 -mx-1"
            >
              <SectionLabel>{tl('features')} ({state.features.length})</SectionLabel>
            </button>
            {expandedSections.features && (
              <div className="ml-2 mt-1.5 space-y-1 border-l border-[#e5e7eb] pl-3">
                {state.features.map((f, i) => (
                  <div key={i} className="bg-[#f9fafb] p-2.5 rounded text-xs space-y-0.5">
                    <InfoRow label={tl('key')}>{f.key}</InfoRow>
                    {f.title && (
                      <InfoRow label={tl('title')}>
                        {t(`subscription.features.${f.key}`, { defaultValue: f.title })}
                      </InfoRow>
                    )}
                    {f.description && (
                      <InfoRow label={tl('description')} muted>
                        {t(`subscription.features.descriptions.${f.key}`, { defaultValue: f.description })}
                      </InfoRow>
                    )}
                    {f.isActive !== undefined && (
                      <InfoRow label={tl('active')}>{f.isActive ? tl('yes') : tl('no')}</InfoRow>
                    )}
                    {f.badge && <InfoRow label={tl('badge')}>{f.badge}</InfoRow>}
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* Prices Section */}
        {state.prices && Array.isArray(state.prices) && state.prices.length > 0 && (
          <>
            <div className="h-px bg-[#f0f2f4] my-2" />
            <button
              onClick={() => toggleSection('prices')}
              className="w-full text-left py-1.5 hover:bg-[#f9fafb] transition-colors rounded px-1 -mx-1"
            >
              <SectionLabel>{tl('prices')} ({state.prices.length})</SectionLabel>
            </button>
            {expandedSections.prices && (
              <div className="ml-2 mt-1.5 space-y-1 border-l border-[#e5e7eb] pl-3">
                {state.prices.map((p, i) => (
                  <div key={i} className="bg-[#f9fafb] p-2.5 rounded text-xs space-y-0.5">
                    <InfoRow label={tl('amount')}>{p.amount}</InfoRow>
                    {p.currency && <InfoRow label={tl('currency')}>{p.currency.toUpperCase()}</InfoRow>}
                    {p.interval && <InfoRow label={tl('interval')}>{p.interval}</InfoRow>}
                    {p.stripePriceId && <InfoRow label={tl('stripePriceId')}>{p.stripePriceId}</InfoRow>}
                    {p.discount && <InfoRow label={tl('discount')}>{p.discount}%</InfoRow>}
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* Profit Split Section */}
        {state.profitSplit && typeof state.profitSplit === 'object' && (
          <>
            <div className="h-px bg-[#f0f2f4] my-2" />
            <button
              onClick={() => toggleSection('profitSplit')}
              className="w-full text-left py-1.5 hover:bg-[#f9fafb] transition-colors rounded px-1 -mx-1"
            >
              <SectionLabel>{tl('profitSplit')}</SectionLabel>
            </button>
            {expandedSections.profitSplit && (
              <div className="ml-2 mt-1.5 space-y-0.5 border-l border-[#e5e7eb] pl-3">
                {Object.entries(state.profitSplit).map(([key, value]) => (
                  <InfoRow key={key} label={key}>
                    {value}%
                  </InfoRow>
                ))}
              </div>
            )}
          </>
        )}

        {/* Sync Status Section */}
        {state.syncStatus && typeof state.syncStatus === 'object' && (
          <>
            <div className="h-px bg-[#f0f2f4] my-2" />
            <button
              onClick={() => toggleSection('syncStatus')}
              className="w-full text-left py-1.5 hover:bg-[#f9fafb] transition-colors rounded px-1 -mx-1"
            >
              <SectionLabel>{tl('syncStatus')}</SectionLabel>
            </button>
            {expandedSections.syncStatus && (
              <div className="ml-2 mt-1.5 space-y-0.5 border-l border-[#e5e7eb] pl-3">
                {Object.entries(state.syncStatus).map(([key, value]) => (
                  <InfoRow key={key} label={key} muted>
                    {formatValue(value)}
                  </InfoRow>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default SubscriptionStateCard;
